import { useMemo, useState } from "react";
import { SortOrder } from "antd5/es/table/interface";
import { Flex, Text } from "styles/utility-components";

import { numberFormatLong } from "components/app_layout/Typography/Numbers";
import { RelativeDateCell } from "components/table_components/RelativeDateCell";
import EmptyState from "components/ui/empty_state/EmptyState";
import UserInitials from "components/ui/form_components/UserInitials";
import { InputGroup, InputGroupAddon, InputGroupInput } from "components/ui/input_group/InputGroup";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { dateSort, numberSort, stringSort } from "lib/columnSort";
import { Table } from "lib/core_components/Table";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import { UserUsage } from "lib/hooks/api/usage/useCompanyUserUsage";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { FeatureEntityType } from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";
import { FEATURE_USAGE_CONFIG, isFeatureShown } from "./featureUsageConfig";
import { Swatch } from "./FeatureUsageLegend";

// Repeating the first direction makes a third click flip it back rather than clear the sort.
const ASCEND_FIRST: SortOrder[] = ["ascend", "descend", "ascend"];
const DESCEND_FIRST: SortOrder[] = ["descend", "ascend", "descend"];

/** Every feature, in the order the pie's colours run. */
const FEATURES = Object.keys(FEATURE_USAGE_CONFIG) as FeatureEntityType[];

/** A user's name, falling back to their email when the profile has none. */
function displayName({ firstName, lastName, email }: UserUsage): string {
  return `${firstName} ${lastName}`.trim() || email;
}

/** A count of days, right-aligned so the figures line up, most first when sorted. */
function daysColumn(
  key: string,
  title: React.ReactNode,
  days: (user: UserUsage) => number,
): ColumnType<UserUsage> {
  return {
    key,
    title,
    align: "right",
    sorter: numberSort(days),
    sortDirections: DESCEND_FIRST,
    render: (_: unknown, user: UserUsage) => (
      <Text color={days(user) ? undefined : "sysTextPlaceholder"}>
        {numberFormatLong(days(user))}
      </Text>
    ),
  };
}

/** The user and their activity, ahead of a column per feature. */
const USER_COLUMNS: ColumnType<UserUsage>[] = [
  {
    key: "user",
    title: "User",
    sizeConfig: { xlarge: 280, large: 260, medium: 240, small: 240 },
    sorter: stringSort(displayName),
    sortDirections: ASCEND_FIRST,
    render: (_: unknown, user: UserUsage) => (
      <Flex alignItems="center" gap={8}>
        <UserInitials firstName={user.firstName} lastName={user.lastName} showTooltip={false} />
        <Flex column>
          <Text>{displayName(user)}</Text>
          <Text description color="sysTextSecondary">
            {user.email}
          </Text>
        </Flex>
      </Flex>
    ),
  },
  {
    key: "team",
    title: "Team",
    dataIndex: "teamName",
    sorter: stringSort((user: UserUsage) => user.teamName),
    sortDirections: ASCEND_FIRST,
  },
  {
    key: "lastActive",
    title: "Last active",
    // Users never active count as the oldest, so they sink when the most recent come first.
    sorter: dateSort((user: UserUsage) => user.lastActiveAt),
    sortDirections: DESCEND_FIRST,
    render: (_: unknown, user: UserUsage) => (
      <RelativeDateCell date={user.lastActiveAt} minWidth={100} />
    ),
  },
  {
    ...daysColumn("activeDays", "Active days", (user) => user.activeDays),
    defaultSortOrder: "descend",
  },
  daysColumn("mcpDays", "MCP days", (user) => user.mcpDays),
];

/** A feature's days used, headed by its name and pie colour. */
function featureColumn(entityType: FeatureEntityType): ColumnType<UserUsage> {
  const { label, color } = FEATURE_USAGE_CONFIG[entityType];
  const title = (
    <Flex alignItems="center" gap={6}>
      <Swatch swatchColor={color} />
      {label}
    </Flex>
  );
  return daysColumn(entityType, title, (user) => user.daysUsed[entityType]);
}

type Props = {
  users: UserUsage[];
  /** Whether notice use has a column per stage rather than one for Notices. */
  splitNoticesByStage: boolean;
  isLoading: boolean;
  isError: boolean;
};

/** Each user's activity and feature use, searchable by name or email and sortable by any column. */
function UserUsageTable({ users, splitNoticesByStage, isLoading, isError }: Props) {
  const columns = useMemo(
    () => [
      ...USER_COLUMNS,
      ...FEATURES.filter((entityType) => isFeatureShown(entityType, splitNoticesByStage)).map(
        featureColumn,
      ),
    ],
    [splitNoticesByStage],
  );

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useTablePageSize("usage-users-table");

  // A new response, for another range or team, starts from the first page but keeps the search
  // and sort.
  const [pagedUsers, setPagedUsers] = useState(users);
  if (users !== pagedUsers) {
    setPagedUsers(users);
    setPage(1);
  }

  const shownUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) =>
      `${displayName(user)} ${user.email}`.toLowerCase().includes(query),
    );
  }, [users, search]);

  return (
    <Flex column gap={24}>
      <Flex width={320}>
        <InputGroup>
          <InputGroupAddon align="inline-start" aria-label="Search">
            <UIcon icon="searchSm" />
          </InputGroupAddon>
          <InputGroupInput
            name="search-users"
            aria-label="Search users"
            placeholder="Search users"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </InputGroup>
      </Flex>
      {isLoading ? (
        <SkeletonText width="80px" />
      ) : (
        <Text>{pluralise(shownUsers.length, "user", true)}</Text>
      )}
      <Table
        ariaLabel="User usage"
        columns={columns}
        dataSource={shownUsers}
        rowKey="userId"
        loading={isLoading}
        isError={isError}
        locale={{ emptyText: <EmptyState heading="No users match your filters" /> }}
        pagination={{
          current: page,
          pageSize,
          pageSizeOptions: [10, 20, 50],
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(nextPage);
          },
        }}
        onChange={(_pagination, _filters, _sorter, { action }) => {
          // Also fires on pagination, which the pagination onChange above handles.
          if (action === "sort") setPage(1);
        }}
      />
    </Flex>
  );
}

export default UserUsageTable;
